import { miraIcons, type IconPrimitive, type MiraIconName } from '@mira/clients-shared';
import { useState, type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ImgHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SVGProps, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { webImage2IconAssets } from '../../assets/catalog.js';

export function Button(props: ButtonHTMLAttributes<HTMLButtonElement> & { busy?: boolean; tone?: 'primary' | 'secondary' | 'danger' }) {
  const { busy = false, children, tone = 'primary', ...rest } = props;
  return (
    <button {...rest} aria-busy={busy || rest['aria-busy'] || undefined} className={join('mira-button', rest.className)} data-busy={busy || undefined} data-tone={tone}>
      {busy ? <LoadingRing /> : null}
      <span>{children}</span>
    </button>
  );
}

export function ButtonLink(props: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: 'primary' | 'secondary' | 'text' }) {
  const { variant = 'secondary', ...rest } = props;
  const className = variant === 'primary' ? 'mira-primary-link-button' : variant === 'text' ? 'mira-text-action' : 'mira-button';
  return <a {...rest} className={join(className, rest.className)} data-tone={variant === 'secondary' ? 'secondary' : undefined} />;
}

export function IconButton(props: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  const { children, label, ...rest } = props;
  return <button {...rest} className={join('mira-icon-button', rest.className)} aria-label={label}>{children ? <IconGlyph value={children} /> : null}</button>;
}

export function MiraIcon(props: SVGProps<SVGSVGElement> & { name: MiraIconName; title?: string }) {
  const { className, name, title, ...rest } = props;
  const imageSrc = webImage2IconAssets[name];
  if (imageSrc) {
    return (
      <img
        {...(rest as ImgHTMLAttributes<HTMLImageElement>)}
        alt={title || ''}
        aria-hidden={title ? undefined : true}
        className={join('mira-image2-icon', className)}
        src={imageSrc}
        title={title}
      />
    );
  }

  const icon = miraIcons[name];
  return (
    <svg
      {...rest}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      className={join('mira-svg-icon', className)}
      fill="none"
      role={title ? 'img' : undefined}
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.8"
      viewBox={icon.viewBox}
    >
      {title ? <title>{title}</title> : null}
      {icon.primitives.map((primitive, index) => renderIconPrimitive(primitive, index))}
    </svg>
  );
}

export function IconGlyph(props: { value: ReactNode; className?: string }) {
  if (typeof props.value !== 'string') return <>{props.value}</>;
  const name = glyphIconNames[props.value.trim()];
  if (!name) return <span className={props.className} aria-hidden="true">{props.value}</span>;
  return <MiraIcon className={props.className} name={name} />;
}

type FormControlShellProps = {
  className?: string;
  help?: ReactNode;
  hint?: ReactNode;
  icon?: ReactNode;
  label: string;
  trailing?: ReactNode;
};

export function Field(props: InputHTMLAttributes<HTMLInputElement> & FormControlShellProps) {
  const { className, help, hint, icon, label, trailing, ...rest } = props;
  return (
    <FormControlShell className={className} help={hint ?? help} icon={icon} label={label} trailing={trailing}>
      <input aria-label={label} {...rest} />
    </FormControlShell>
  );
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement> & FormControlShellProps) {
  const { className, help, hint, icon, label, trailing, ...rest } = props;
  return (
    <FormControlShell className={className} help={hint ?? help} icon={icon} label={label} trailing={trailing}>
      <textarea aria-label={label} {...rest} />
    </FormControlShell>
  );
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement> & FormControlShellProps) {
  const { className, help, hint, icon, label, trailing, children, ...rest } = props;
  return (
    <FormControlShell className={className} help={hint ?? help} icon={icon} label={label} trailing={trailing}>
      <select aria-label={label} {...rest}>{children}</select>
    </FormControlShell>
  );
}

export function AttachmentField(props: InputHTMLAttributes<HTMLInputElement> & FormControlShellProps) {
  return <Field {...props} type={props.type || 'file'} />;
}

export function SearchInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={join('mira-search-input', props.className)} type="search" />;
}

export function SegmentedTabs<T extends string>(props: { buttonClassName?: string; className?: string; value: T; items: readonly { id: T; label: string }[]; onChange: (id: T) => void }) {
  return (
    <div className={join('mira-segmented-tabs', props.className)} role="tablist">
      {props.items.map((item) => (
        <button className={props.buttonClassName} data-active={props.value === item.id || undefined} key={item.id} onClick={() => props.onChange(item.id)} role="tab" type="button">
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function Card(props: { children: ReactNode; className?: string }) {
  return <section className={join('mira-card', props.className)}>{props.children}</section>;
}

export function Sheet(props: { children: ReactNode; className?: string; onClose?: () => void; scrimClassName?: string; title: string }) {
  return (
    <div className={join('mira-modal-scrim', props.scrimClassName)}>
      <section className={join('mira-sheet', props.className)} role="dialog" aria-modal="true" aria-label={props.title}>
        {props.onClose ? <IconButton className="mira-sheet-close" label={`Close${props.title}`} onClick={props.onClose} type="button">×</IconButton> : null}
        {props.children}
      </section>
    </div>
  );
}

export function Toast(props: { children: ReactNode; tone?: 'neutral' | 'success' | 'error' }) {
  return <div className="mira-toast" data-tone={props.tone || 'neutral'}>{props.children}</div>;
}

export function LoadingRing() {
  return <span className="mira-loading-ring" aria-hidden="true" />;
}

export function Avatar(props: { className?: string; decorative?: boolean; label: string; imageUrl?: string | null }) {
  const [failed, setFailed] = useState(false);
  if (props.imageUrl && !failed) {
    return (
      <span className={join('mira-avatar mira-person-avatar', props.className)}>
        <img className="mira-avatar-image" src={props.imageUrl} alt={props.decorative ? '' : props.label} onError={() => setFailed(true)} />
      </span>
    );
  }
  return <span className={join('mira-avatar mira-avatar-initials', props.className)} aria-hidden={props.decorative || undefined} aria-label={props.decorative ? undefined : props.label}>{initials(props.label)}</span>;
}

export function EmptyState(props: { title: string; body?: string; actionHref?: string; actionLabel?: string }) {
  return <StateBlock tone="empty" title={props.title} body={props.body} actionHref={props.actionHref} actionLabel={props.actionLabel} />;
}

export function ErrorState(props: { title: string; body?: string; actionHref?: string; actionLabel?: string }) {
  return <StateBlock tone="error" title={props.title} body={props.body} actionHref={props.actionHref} actionLabel={props.actionLabel} />;
}

export function LoadingState(props: { title?: string; body?: string }) {
  return <StateBlock tone="loading" title={props.title || 'Loading'} body={props.body} />;
}

export function List(props: { children: ReactNode; className?: string }) {
  return <div className={join('mira-list', props.className)}>{props.children}</div>;
}

export function ListRow(props: { children: ReactNode; href?: string; onClick?: () => void }) {
  if (props.href) return <a className="mira-list-row" href={props.href}>{props.children}</a>;
  if (props.onClick) return <button className="mira-list-row" onClick={props.onClick} type="button">{props.children}</button>;
  return <div className="mira-list-row">{props.children}</div>;
}

function StateBlock(props: { tone: string; title: string; body?: string; actionHref?: string; actionLabel?: string }) {
  return (
    <section className="mira-state" data-tone={props.tone}>
      {props.tone === 'loading' ? <LoadingRing /> : null}
      <h2>{props.title}</h2>
      {props.body ? <p>{props.body}</p> : null}
      {props.actionHref && props.actionLabel ? <a className="mira-state-action" href={props.actionHref}>{props.actionLabel}</a> : null}
    </section>
  );
}

function FormControlShell(props: FormControlShellProps & { children: ReactNode }) {
  return (
    <label className={props.className || 'mira-field'}>
      <span>{props.label}</span>
      <div>
        {props.icon ? <strong aria-hidden="true"><IconGlyph value={props.icon} /></strong> : null}
        {props.children}
        {props.trailing ? <em aria-hidden="true"><IconGlyph value={props.trailing} /></em> : null}
      </div>
      {props.help ? <small>{props.help}</small> : null}
    </label>
  );
}

const glyphIconNames: Record<string, MiraIconName> = {
  '$': 'semantic.spark',
  '!': 'semantic.spark',
  '?': 'semantic.insight',
  '‹': 'action.back',
  '›': 'action.next',
  '→': 'action.next',
  '↩': 'action.back',
  '×': 'action.close',
  '⌕': 'action.search',
  '○': 'semantic.spark',
  '◯': 'semantic.spark',
  '◌': 'semantic.spark',
  '◉': 'semantic.spark',
  '◒': 'semantic.topic',
  '□': 'semantic.note',
  '▯': 'semantic.note',
  '▰': 'semantic.lock',
  '▣': 'semantic.note',
  '▧': 'semantic.note',
  '▱': 'semantic.lock',
  '▤': 'semantic.topic',
  '☁': 'semantic.memory',
  '☰': 'semantic.topic',
  '☾': 'nav.today',
  '☷': 'semantic.memory',
  '☼': 'nav.today',
  '◷': 'semantic.memory',
  '◎': 'semantic.topic',
  '⌂': 'nav.today',
  '⌁': 'semantic.spark',
  '⌖': 'action.search',
  '⌫': 'action.delete',
  '⌄': 'action.next',
  '↻': 'semantic.memory',
  '✓': 'action.check',
  '⚠': 'semantic.insight',
  '⌑': 'semantic.relationship',
  '＋': 'action.add',
  '✎': 'action.edit',
  '✧': 'semantic.spark',
  '♡': 'semantic.relationship',
  '♥': 'semantic.relationship',
  '♧': 'semantic.relationship',
  '♙': 'action.settings',
  '♛': 'semantic.spark',
  '♀': 'action.settings',
  '♂': 'action.settings',
  '⇥': 'action.logout',
  '§': 'semantic.note',
  '✦': 'semantic.spark',
  '⋮': 'action.filter',
  '△': 'semantic.insight',
};

function join(...items: Array<string | undefined>): string {
  return items.filter(Boolean).join(' ');
}

function renderIconPrimitive(primitive: IconPrimitive, index: number) {
  if (primitive.kind === 'circle') return <circle key={index} cx={primitive.cx} cy={primitive.cy} r={primitive.r} />;
  if (primitive.kind === 'line') return <line key={index} x1={primitive.x1} y1={primitive.y1} x2={primitive.x2} y2={primitive.y2} />;
  return <path key={index} d={primitive.d} />;
}

function initials(label: string): string {
  return label.trim().split(/\s+/u).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'M';
}
